import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input, Message, Tab, Table } from 'semantic-ui-react';

import { useForm } from '../../hooks';

import styles from './SettingPricePane.module.scss';

const createMessage = (error) => {
  if (!error) {
    return error;
  }

  switch (error.message) {
    case 'Setting not found':
      return {
        type: 'error',
        content: 'common.settingPriceNotFound',
      };
    case 'Setting existed':
      return {
        type: 'error',
        content: 'common.settingPriceExisted',
      };
    default:
      return {
        type: 'warning',
        content: 'common.unknownError',
      };
  }
};

const PricePane = React.memo(
  ({ isEditable, name, defaultData, settingPrice, isSubmitting, error, onCreate, onUpdate }) => {
    const [t] = useTranslation();
    const [fields, setFields] = useState([]);

    const [data, handleFieldChange, setData] = useForm(() => ({
      ...defaultData,
    }));

    const message = useMemo(() => createMessage(error), [error]);

    const descriptionField = useRef(null);
    const currencyField = useRef(null);
    const unitNameField = useRef(null);

    const handleAdd = () => {
      const values = [...fields];
      values.push({ min: 0, max: 0, price: 0 });
      setFields(values);
    };

    const handleRemove = (i) => {
      const values = [...fields];
      values.splice(i, 1);
      setFields(values);
    };

    const handleChange = (i, event) => {
      const values = [...fields];
      values[i][event.target.name] = event.target.value;
      setFields(values);
    };

    const [descriptionError, setDescriptionError] = useState(false);
    const [unitNameError, setUnitNameError] = useState(false);
    const [currencyError, setCurrencyError] = useState(false);

    const handleSubmit = useCallback(() => {
      const cleanData = {
        ...data,
        id: settingPrice?.id || '',
        name,
        prices: fields,
      };
      let errorSubmit;
      if (!cleanData.description) {
        setDescriptionError(true);
        errorSubmit = true;
      }
      if (!cleanData.unitName) {
        setUnitNameError(true);
        errorSubmit = true;
      }
      if (!cleanData.currency) {
        setCurrencyError(true);
        errorSubmit = true;
      }
      if (errorSubmit) return;

      if (settingPrice.id) {
        onUpdate(cleanData);
      } else {
        onCreate(cleanData);
      }
    }, [data, fields, name, onCreate, onUpdate, settingPrice.id]);

    useEffect(() => {
      const fieldPrices = (settingPrice && settingPrice.data.prices) || [];
      setFields(fieldPrices);
    }, [settingPrice]);

    useEffect(() => {
      setData({ ...defaultData });
    }, [defaultData, setData]);

    return (
      <Tab.Pane className={styles.wrapper}>
        {message && (
          <Message
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...{
              [message.type]: true,
            }}
            compact
            visible
            content={t(message.content)}
          />
        )}
        <Form onSubmit={handleSubmit}>
          <Form.Group widths="equal">
            <Form.Field
              required
              error={descriptionError}
              onFocus={() => setDescriptionError(false)}
            >
              <label htmlFor="description">{t('common.settingPrice.description')}</label>
              <Input
                fluid
                ref={descriptionField}
                name="description"
                defaultValue={data.description}
                readOnly={isSubmitting}
                className={styles.field}
                onChange={handleFieldChange}
                disabled={!isEditable}
              />
            </Form.Field>
            <Form.Field required error={unitNameError} onFocus={() => setUnitNameError(false)}>
              <label htmlFor="unitName">{t('common.settingPrice.unitName')}</label>
              <Input
                fluid
                ref={unitNameField}
                name="unitName"
                value={data.unitName}
                readOnly={isSubmitting}
                className={styles.field}
                onChange={handleFieldChange}
                disabled={!isEditable}
              />
            </Form.Field>
            <Form.Field required error={currencyError} onFocus={() => setCurrencyError(false)}>
              <label htmlFor="currency">{t('common.settingPrice.currency')}</label>
              <Input
                fluid
                ref={currencyField}
                name="currency"
                value={data.currency}
                readOnly={isSubmitting}
                className={styles.field}
                onChange={handleFieldChange}
                disabled={!isEditable}
              />
            </Form.Field>
          </Form.Group>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{t('common.settingPrice.level')}</Table.HeaderCell>
                <Table.HeaderCell>{t('common.settingPrice.min')}</Table.HeaderCell>
                <Table.HeaderCell>{t('common.settingPrice.max')}</Table.HeaderCell>
                <Table.HeaderCell>{t('common.settingPrice.price')}</Table.HeaderCell>
                <Table.HeaderCell>&nbsp;</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {fields.map((field, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Table.Row key={index}>
                  <Table.Cell singleLine>{index + 1}</Table.Cell>
                  <Table.Cell singleLine>
                    <Input
                      fluid
                      name="min"
                      defaultValue={field.min}
                      readOnly={isSubmitting}
                      className={styles.field}
                      onChange={(e) => handleChange(index, e)}
                      disabled={!isEditable}
                    />
                  </Table.Cell>
                  <Table.Cell singleLine>
                    <Input
                      fluid
                      name="max"
                      defaultValue={field.max}
                      readOnly={isSubmitting}
                      className={styles.field}
                      onChange={(e) => handleChange(index, e)}
                      disabled={!isEditable}
                    />
                  </Table.Cell>
                  <Table.Cell singleLine>
                    <Input
                      fluid
                      name="price"
                      defaultValue={field.price}
                      readOnly={isSubmitting}
                      className={styles.field}
                      onChange={(e) => handleChange(index, e)}
                      disabled={!isEditable}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      className={styles.buttonRemoveLevel}
                      type="button"
                      onClick={() => handleRemove(index)}
                      disabled={!isEditable}
                    >
                      {t('common.settingPrice.removeLevel')}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
            <Table.Footer fullWidth>
              <Table.Row>
                <Table.HeaderCell colSpan="5">
                  <Button
                    floated="right"
                    color="green"
                    icon="checkmark"
                    content={t('action.save')}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  />
                  <Button
                    className={styles.buttonAddLevel}
                    type="button"
                    onClick={() => handleAdd()}
                    disabled={!isEditable}
                  >
                    {t('common.settingPrice.addLevel')}
                  </Button>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </Form>
      </Tab.Pane>
    );
  },
);

PricePane.propTypes = {
  isEditable: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  defaultData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  isSubmitting: PropTypes.bool.isRequired,
  settingPrice: PropTypes.objectOf(PropTypes.any).isRequired,
  error: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

PricePane.defaultProps = {
  error: undefined,
};

export default PricePane;
